import { CommonModule } from '@angular/common';
import {
	Component,
	DestroyRef,
	EventEmitter,
	Input,
	Output,
	Signal,
	effect,
	inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	FormBuilder,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
	Validators
} from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import {
	DateAdapter,
	MAT_DATE_FORMATS,
	MAT_DATE_LOCALE,
	provideNativeDateAdapter
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { CustomDateAdapter } from '../../../../shared/utils/adapters/custom-date-adapter';
import { FormErrorsPipe } from '../../../../shared/utils/pipes/form-errors.pipe';

const DATE_INPUT_FORMAT = 'DD/MM/YYYY';

const CUSTOM_DATE_FORMATS = {
	parse: { dateInput: DATE_INPUT_FORMAT },
	display: {
		dateInput: DATE_INPUT_FORMAT,
		monthYearLabel: 'MMM YYYY',
		dateA11yLabel: DATE_INPUT_FORMAT,
		monthYearA11yLabel: 'MMMM YYYY'
	}
};

@Component({
	selector: 'app-register-personal-info',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatCardModule,
		MatSelectModule,
		MatDatepickerModule,
		FormErrorsPipe
	],
	providers: [
		provideNativeDateAdapter(),
		{ provide: DateAdapter, useClass: CustomDateAdapter },
		{ provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
		{ provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS }
	],
	templateUrl: './register-personal-info.html',
	styleUrl: './register-personal-info.scss'
})
export class RegisterPersonalInfoComponent {
	@Input({ required: true }) requestForm!: Signal<boolean>;
	@Input({ required: true }) userData!: Signal<any | null>;

	@Output() formData = new EventEmitter<any>();

	personalInfoForm: FormGroup;

	tiposSexo = [
		{ id: 'Masculino', description: 'Masculino' },
		{ id: 'Femenino', description: 'Femenino' },
		{ id: 'Otro', description: 'Otro' }
	];

	nacionalidades = [
		{ id: 'Argentina', description: 'Argentina' },
		{ id: 'Extranjero', description: 'Extranjero' }
	];

	maxDate = new Date();

	private fb = inject(FormBuilder);
	private destroyRef = inject(DestroyRef);

	constructor() {
		this.personalInfoForm = this.fb.group({
			tipoDeDocumento: ['', Validators.required],
			numeroDocumento: ['', [Validators.required, Validators.pattern(/^\d{7,8}$/)]],
			cuil: ['', Validators.pattern(/^\d{11}$/)],
			username: [''],
			nombre: ['', [Validators.required, Validators.minLength(2)]],
			apellido: ['', [Validators.required, Validators.minLength(2)]],
			fechaDeNacimiento: ['', Validators.required],
			sexo: ['', Validators.required],
			nacionalidad: ['']
		});

		effect(() => {
			const data = this.userData();
			if (!data) return;
			this.patchFormWithData(data);
		});

		effect(() => {
			if (this.requestForm()) {
				this.sendFormData();
			}
		});
	}

	private patchFormWithData(data: any): void {
		let fechaNacimiento = null;

		if (data.fechaDeNacimiento) {
			if (data.fechaDeNacimiento instanceof Date) {
				fechaNacimiento = data.fechaDeNacimiento;
			} else {
				const [year, month, day] = String(data.fechaDeNacimiento).split('-');
				fechaNacimiento = new Date(+year, +month - 1, +day);
			}
		}

		this.personalInfoForm.patchValue(
			{
				tipoDeDocumento: data.tipoDeDocumento ?? '',
				numeroDocumento: data.numeroDocumento ?? '',
				cuil: data.cuil ?? data.username ?? '',
				username: data.username ?? '',
				nombre: data.nombre ?? '',
				apellido: data.apellido ?? '',
				fechaDeNacimiento: fechaNacimiento,
				sexo: data.sexo ?? '',
				nacionalidad: data.nacionalidad ?? ''
			},
			{ emitEvent: false }
		);

		this.personalInfoForm.get('tipoDeDocumento')?.disable();
		this.personalInfoForm.get('numeroDocumento')?.disable();
		this.personalInfoForm.get('cuil')?.disable();
		this.personalInfoForm.get('username')?.disable();
		this.personalInfoForm.get('nombre')?.disable();
		this.personalInfoForm.get('apellido')?.disable();
	}

	private sendFormData(): void {
		if (this.personalInfoForm.invalid) {
			this.personalInfoForm.markAllAsTouched();
			return;
		}

		this.formData.emit(this.personalInfoForm.getRawValue());
	}
}
